import {
    AnimationBaseController,
    IAnimationControllerConfiguration,
    Inject,
    SoundManager,
} from '@denishussar/html5-game-engine';
import { GameModel, GameStateEnum } from '../../Models/Game/GameModel';
import { GameController } from './GameController';
import { GameLogic } from '../../Logic/GameLogic';
import { AnimationContents } from '../../../AnimationContents';
import { FlyersStandController } from './MiniGames/flyers_stand/FlyersStandController';
import { GamePadContainerController } from '../UI/GamePad/GamePadContainerController';
import { UIController } from '../UI/UIController';
import { RootController as ElectricityRootController } from './MiniGames/electricity/Controllers/RootController';
import { RootController as DinnerRootController } from './MiniGames/dinner/Controllers/RootController';
import { RootController as SendLetterRootController } from './MiniGames/send_letter/Controllers/RootController';
import { ExchangeRootController } from './MiniGames/currency_exchange/Controllers/ExchangeRootController';
import { RootController as LockCellRootController } from './MiniGames/lock_cell/Controllers/RootController';
import { RootController as LoanGameRootController } from './MiniGames/loan_game/Controllers/RootController';
import { MiniGameTypeEnum } from '../../Models/Game/MiniGameModel';
import { PrintCardController } from './MiniGames/print_card/Controllers/PrintCardController';
import { CardsRootController as BlockCardsController } from './MiniGames/block_cards/Controllers/CardsRootController';
import { BussinesTripRootController } from './MiniGames/bussines_trip/Controllers/BussinesTripRootController';
import { PauseController } from '../UI/PauseScreen/PauseController';
import { SettingsController } from '../Menu/Modals/Settings/SettingsController';
import { RulesController } from '../Menu/Modals/Rules/RulesController';
import { GameEndController } from '../UI/GameEnd/GameEndController';
import { UIOverlay } from '../UI/UIOverlay';
import { RestartController } from '../UI/RestartScreen/RestartController';
import { AchievementsController } from '../Menu/Modals/Achievements/AchievementsController';
import { MiniMapController } from '../UI/MiniMapScreen/MiniMapController';
import { UIElectricityOverlay } from '../UI/UIElectricityOverlay';
import { searchParam } from '../../Common/Utils';
import { PlayerNameController } from '../UI/PlayerName/PlayerNameController';
import { LeaderboardController } from '../Menu/Modals/Leaderboard/LeaderboardController';
import { SoundConstants } from '../../Constants/SoundConstants';

export interface IGameScreenControllerOptions {}

export class GameScreenController extends AnimationBaseController {
    $model: GameModel;

    public constructor(
        @Inject private _options: IGameScreenControllerOptions,
        @Inject private _gameLogic: GameLogic,
        @Inject private _soundManager: SoundManager
    ) {
        super('GameScreenController');
    }

    protected getConfiguration(): IAnimationControllerConfiguration {
        return <IAnimationControllerConfiguration>{
            animationContent: AnimationContents.GAME_SCREEN_JSON,
            libraryName: 'root',
            modelWatch: {
                showMiniGame: (showMiniGame: boolean) => {
                    if (showMiniGame) {
                        let view = null;
                        let model = null;

                        switch (this.$model.currMiniGame.type) {
                            case MiniGameTypeEnum.stand_flyers:
                                view = FlyersStandController;
                                model = this.$model.currMiniGame;
                                break;
                            case MiniGameTypeEnum.electricity:
                                view = ElectricityRootController;
                                model = this.$model.ElectricityModel;
                                break;
                            case MiniGameTypeEnum.approve_the_loan:
                                view = LoanGameRootController;
                                model = this.$model.LoanModel;
                                break;
                            case MiniGameTypeEnum.block_cards:
                                view = BlockCardsController;
                                model = this.$model.BlockCardsModel;
                                break;
                            case MiniGameTypeEnum.lock_cell:
                                view = LockCellRootController;
                                model = this.$model.LockCellModel;
                                break;
                            case MiniGameTypeEnum.dinner:
                                view = DinnerRootController;
                                model = this.$model.DinnerModel;
                                break;
                            case MiniGameTypeEnum.send_a_letter:
                                view = SendLetterRootController;
                                model = this.$model.SendLetterModel;
                                break;
                            case MiniGameTypeEnum.currency_exchange:
                                view = ExchangeRootController;
                                model = this.$model.ExchangeModel;
                                break;
                            case MiniGameTypeEnum.print_card:
                                view = PrintCardController;
                                model = this.$model.PrintCardModel;
                                break;
                            case MiniGameTypeEnum.bussines_trip:
                                view = BussinesTripRootController;
                                model = this.$model.BussinesTripModel;
                                break;
                        }

                        this.addChildController(
                            {
                                view: view,
                                model: model,
                            },
                            'MiniGames'
                        );
                    } else {
                        this.removeChildControllerByName('MiniGames');
                    }
                },
                gameState: async (state: GameStateEnum) => {
                    const screen = this.$childControllers.get('pause');
                    if (screen) {
                        await this.removeChildControllerByNameAsync('pause');
                    }
                    switch (state) {
                        case GameStateEnum.pause:
                            this.addChildController(
                                {
                                    view: PauseController,
                                    model: this.$model,
                                },
                                'pause'
                            );
                            break;

                        case GameStateEnum.settings:
                            this.addChildController(
                                {
                                    view: SettingsController,
                                    model: this.$model,
                                },
                                'pause'
                            );
                            break;

                        case GameStateEnum.achievements:
                            this.addChildController(
                                {
                                    view: AchievementsController,
                                    model: this.$model,
                                },
                                'pause'
                            );
                            break;

                        case GameStateEnum.rules:
                            this.addChildController(
                                {
                                    view: RulesController,
                                    model: this.$model,
                                },
                                'pause'
                            );
                            break;

                        case GameStateEnum.restart:
                            this.addChildController(
                                {
                                    view: RestartController,
                                    model: this.$model,
                                    options: {
                                        toMenu: false,
                                    },
                                },
                                'pause'
                            );
                            break;

                        case GameStateEnum.restartToMenu:
                            this.addChildController(
                                {
                                    view: RestartController,
                                    model: this.$model,
                                    options: {
                                        toMenu: true,
                                    },
                                },
                                'pause'
                            );
                            break;

                        case GameStateEnum.gameEnd:
                            this.addChildController(
                                {
                                    view: GameEndController,
                                    model: this.$model,
                                },
                                'pause'
                            );
                            break;

                        case GameStateEnum.minimap:
                            this.addChildController(
                                {
                                    view: MiniMapController,
                                    model: this.$model,
                                    options: {
                                        gameController:
                                            this.$childControllers.get(
                                                'GameController'
                                            ),
                                    },
                                },
                                'pause'
                            );
                            break;

                        case GameStateEnum.playerName:
                            this.addChildController(
                                {
                                    view: PlayerNameController,
                                    model: this.$model,
                                },
                                'pause'
                            );
                            break;

                        case GameStateEnum.leaderboards:
                            this.addChildController(
                                {
                                    view: LeaderboardController,
                                    model: this.$model,
                                },
                                'pause'
                            );
                            break;
                    }
                },
            },
        };
    }

    protected async onInitialize() {
        await this.startGame();
    }

    protected onLandscape(): void {}

    protected onPortrait(): void {}

    protected onUpdate(frameRate: number): void {
        this._gameLogic.update(frameRate);
    }

    private async startGame(): Promise<void> {
        if (!this._soundManager.isSoundPlaying(SoundConstants.GAME)) {
            this._soundManager.stop(SoundConstants.GAME);
        }

        this._gameLogic.reset();

        await this.removeChildControllerByNameAsync('GameController');
        await this.removeChildControllerByNameAsync('UIElectricityOverlay');
        await this.removeChildControllerByNameAsync('UIController');
        await this.removeChildControllerByNameAsync(
            'GamePadContainerController'
        );
        await this.removeChildControllerByNameAsync('UIOverlay');

        await this.addChildControllerAsync(
            {
                instanceName: 'map',
                view: GameController,
                model: this.$model,
                options: this._options,
            },
            false,
            'GameController'
        );

        await this.addChildControllerAsync(
            {
                view: UIElectricityOverlay,
                model: this.$model,
            },
            false,
            'UIElectricityOverlay'
        );

        await this.addChildControllerAsync(
            {
                view: UIController,
                model: this.$model,
            },
            false,
            'UIController'
        );

        await this.addChildControllerAsync(
            {
                view: GamePadContainerController,
                model: this.$model.gamePad,
            },
            false,
            'GamePadContainerController'
        );

        await this.addChildControllerAsync(
            {
                view: UIOverlay,
                model: this.$model,
            },
            false,
            'UIOverlay'
        );

        this._gameLogic.init();

        const skipHelpValue = searchParam('skipHelp');
        const skipHelp = skipHelpValue === '1' || skipHelpValue === 'true';

        if (skipHelp) {
            this.$model.isFirstLaunch = false;
        }

        if (this.$model.isFirstLaunch) {
            this._gameLogic.showPauseRules();
        }
    }
}
